import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";
import RichTextEditor from "@/components/RichTextEditor";
import { toast } from "sonner";

const CATEGORIES = ["Wellness Tools", "Courses", "Music", "Art", "Gift Cards", "Enhancer Products"];

export default function ProductFormModal({ product, onClose, onSaved }) {
  const editing = !!product?.id;
  const [form, setForm] = useState({
    title: product?.title || "", category: product?.category || "Enhancer Products",
    type: product?.type || "physical",
    short_description: product?.short_description || "", description: product?.description || "",
    price: product?.price ?? 0, member_discount_pct: product?.member_discount_pct || 0,
    shipping_price: product?.shipping_price || 0, stock: product?.stock || 0,
    track_stock: product?.track_stock || false, digital_url: product?.digital_url || "",
    image: product?.image || "", sku: product?.sku || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      member_discount_pct: parseInt(form.member_discount_pct) || 0,
      shipping_price: parseFloat(form.shipping_price) || 0,
      stock: parseInt(form.stock) || 0,
    };
    try {
      if (editing) await api.put(`/enhancer/products/${product.id}`, payload);
      else await api.post("/enhancer/products", payload);
      toast.success(editing ? "Product updated — pending review." : "Product created — pending admin review.");
      onSaved();
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const isPhysical = form.type === "physical";

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm overflow-y-auto py-10" data-testid="product-form-modal">
      <div className="relative w-full max-w-lg bg-[#F9F7F3] rounded-3xl shadow-2xl p-8 my-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        <h3 className="font-serif text-2xl text-[#1A242B] mb-5">{editing ? "Edit product" : "New product"}</h3>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Product title" value={form.title} onChange={set("title")} data-testid="prod-title" className="eyw-input w-full" />
          <div className="grid grid-cols-2 gap-3">
            <input list="prod-cat-list" value={form.category} onChange={set("category")} placeholder="Category (pick or type your own)" data-testid="prod-category" className="eyw-input w-full" />
            <datalist id="prod-cat-list">{CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
            <select value={form.type} onChange={set("type")} data-testid="prod-type" className="eyw-input w-full">
              <option value="physical">Physical</option>
              <option value="digital">Digital</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-[#4A5D6B]">Price $</label><input type="number" step="0.01" value={form.price} onChange={set("price")} data-testid="prod-price" className="eyw-input w-full" /></div>
            <div><label className="text-xs text-[#4A5D6B]">Member % off</label><input type="number" value={form.member_discount_pct} onChange={set("member_discount_pct")} data-testid="prod-discount" className="eyw-input w-full" /></div>
          </div>
          {isPhysical ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><label className="text-xs text-[#4A5D6B]">Shipping $</label><input type="number" step="0.01" value={form.shipping_price} onChange={set("shipping_price")} data-testid="prod-shipping" className="eyw-input w-full" /></div>
              <div><label className="text-xs text-[#4A5D6B]">Stock</label><input type="number" value={form.stock} onChange={set("stock")} data-testid="prod-stock" className="eyw-input w-full" /></div>
              <label className="flex items-end gap-2 text-xs text-[#4A5D6B] pb-2"><input type="checkbox" checked={form.track_stock} onChange={set("track_stock")} /> Track stock</label>
            </div>
          ) : (
            <input placeholder="Digital download / access URL" value={form.digital_url} onChange={set("digital_url")} data-testid="prod-digital-url" className="eyw-input w-full" />
          )}
          <input placeholder="Short description" value={form.short_description} onChange={set("short_description")} data-testid="prod-short" className="eyw-input w-full" />
          <div>
            <label className="text-xs text-[#4A5D6B] mb-1 block">Full description</label>
            <RichTextEditor value={form.description} onChange={(html) => setForm((f) => ({ ...f, description: html }))} testid="prod-desc" enableBlocks={false} placeholder="Describe this product — add headings, lists, images and video…" minHeight={140} />
          </div>
          <ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} label="Product photo (square)" crop="square" testid="prod-image" />
          <p className="text-[11px] text-[#8FA89B]">Enhancer products use a 90/10 split (10% ecosystem finder's fee). Admin reviews before it goes live.</p>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button type="submit" disabled={loading} data-testid="prod-save" className="eyw-btn w-full disabled:opacity-60">
            {loading && <Loader2 className="animate-spin" size={18} />} {editing ? "Save changes" : "Create product"}
          </button>
        </form>
      </div>
    </div>
  );
}
